import { useCallback, useEffect, useRef, useState } from 'react';
import {
  apiErrorStatus,
  apiErrorText,
  econChronumApi,
  useGetEconChronumLoadQuery,
  useRefreshEconChronumMutation,
  type ChronumLoad,
  type ChronumLoadResult,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import { loadElapsed } from '@entities/chronum';
import { runningLoadOf } from './apiErrors';

export type RefreshOutcome =
  /** Загрузка кончилась: дошла, сбой записи или оборвалась. */
  | { kind: 'finished'; load: ChronumLoad; results: ChronumLoadResult[] }
  /** Загрузку не удалось начать. */
  | { kind: 'failed'; period: string; message: string };

export interface ChronumRefresh {
  run: (period: string) => void;
  /** Следить за идущей загрузкой (из статуса месяца) — начатой не здесь. */
  follow: (load: ChronumLoad) => void;
  /** Загрузка, за ходом которой следит экран; `null` — ни за какой. */
  load: ChronumLoad | null;
  /** Загрузку начали не здесь: коллега, другая вкладка или расписание. */
  attached: boolean;
  /** Запрос «Обновить» ещё не ответил. */
  starting: boolean;
  running: boolean;
  /** Месяц, который загружается сейчас. */
  runningPeriod: string | null;
  /** Секунд с начала загрузки. */
  elapsed: number;
  outcome: RefreshOutcome | null;
  dismiss: () => void;
}

/** Загрузка, за которой следит экран. */
interface Tracked {
  id: string;
  period: string;
  attached: boolean;
}

/** Как часто спрашивать сервер о ходе загрузки. */
const POLL_MS = 1500;

const RELOAD_TAGS = (['STATUS', 'POSITIONS', 'SITES', 'HOURS', 'SNAPSHOTS'] as const).map(
  (id) => ({ type: 'EconChronum', id }) as const,
);

/**
 * «Обновить»: загрузка идёт на сервере, экран только следит за её ходом по
 * `load_id` — закрытая вкладка её не обрывает. Одна загрузка на экран: её
 * запускают шапка и пустое состояние свода, итог показывает шапка. Если за
 * месяц загрузка уже идёт (коллега, другая вкладка, расписание), экран
 * подключается к ней, а не начинает вторую.
 */
export function useChronumRefresh(): ChronumRefresh {
  const dispatch = useAppDispatch();
  const [start, { isLoading: starting }] = useRefreshEconChronumMutation();
  const [tracked, setTracked] = useState<Tracked | null>(null);
  const [startingPeriod, setStartingPeriod] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [outcome, setOutcome] = useState<RefreshOutcome | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /** Загрузки, итог которых уже показан: к ним не подключаемся снова. */
  const finished = useRef(new Set<string>());

  const query = useGetEconChronumLoadQuery(tracked?.id ?? '', {
    skip: !tracked,
    pollingInterval: tracked ? POLL_MS : 0,
  });
  const details = tracked ? query.currentData : undefined;
  const load = details?.load ?? null;
  const loadRunning = load?.status === 'running';

  // Загрузка кончилась — итог, данные экрана перечитать, опрос остановить.
  useEffect(() => {
    if (!details || details.load.status === 'running') return;
    const id = details.load.id;
    if (finished.current.has(id)) return;
    finished.current.add(id);
    setOutcome({ kind: 'finished', load: details.load, results: details.results });
    setTracked(null);
    dispatch(econChronumApi.util.invalidateTags(RELOAD_TAGS));
  }, [details, dispatch]);

  // Загрузки нет на сервере (удалили или ссылка чужая) — не опрашивать вечно.
  const lostId =
    tracked && query.isError && apiErrorStatus(query.error) === 404 ? tracked.id : null;
  useEffect(() => {
    if (!lostId) return;
    finished.current.add(lostId);
    setTracked(null);
    setOutcome({
      kind: 'failed',
      period: '',
      message: 'Загрузка не найдена на сервере — нажмите «Обновить» ещё раз.',
    });
  }, [lostId]);

  // Секундомер — пока загрузка начинается или идёт.
  const ticking = starting || loadRunning || Boolean(tracked && !details);
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [ticking]);

  const follow = useCallback((l: ChronumLoad) => {
    if (l.status !== 'running' || finished.current.has(l.id)) return;
    setTracked((cur) => cur ?? { id: l.id, period: l.period, attached: true });
  }, []);

  function run(period: string) {
    if (starting) return;
    setOutcome(null);
    setStartingPeriod(period);
    setStartedAt(Date.now());
    start({ period })
      .unwrap()
      .then(({ load: started }) =>
        setTracked({ id: started.id, period: started.period, attached: false }),
      )
      .catch((err: unknown) => {
        // За месяц уже идёт загрузка — следим за ней, вторую не начинаем.
        const running = runningLoadOf(err);
        if (running) {
          finished.current.delete(running.id);
          setTracked({ id: running.id, period: running.period, attached: true });
          return;
        }
        setOutcome({
          kind: 'failed',
          period,
          message: apiErrorText(err, 'Не удалось начать загрузку Хронума.'),
        });
      })
      .finally(() => setStartingPeriod(null));
  }

  const running = starting || loadRunning || Boolean(tracked && !details);
  const elapsed = load ? loadElapsed(load, now) : Math.max(0, Math.floor((now - startedAt) / 1000));

  return {
    run,
    follow,
    load: loadRunning ? load : null,
    attached: Boolean(tracked?.attached && loadRunning),
    starting,
    running,
    runningPeriod: running ? (tracked?.period ?? startingPeriod) : null,
    elapsed: running ? elapsed : 0,
    outcome,
    dismiss: () => setOutcome(null),
  };
}
